import { useEffect, useRef, useState, type RefObject } from "react";

type Connection = EventTarget & { saveData?: boolean; effectiveType?: string; downlink?: number };
type Props = { imageRef: RefObject<HTMLImageElement | null>; src: string };

/** Decorative progressive enhancement. The existing image always stays underneath.
 * No src/poster/source is rendered on the server or during hydration.
 */
export default function MobileHeroVideo({ imageRef, src }: Props) {
  // Mount only after hydration. Both accepted and held-OFF documents retain
  // identical image-first SSR; viewport-specific state never replaces SSR nodes.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  return mounted ? <MobileHeroPlayback imageRef={imageRef} src={src} /> : null;
}

function MobileHeroPlayback({ imageRef, src }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const image = imageRef.current;
    // Without reliable visibility observation, retain the image.
    if (!video || !image || !window.IntersectionObserver || !window.matchMedia) return;
    const mobile = window.matchMedia("(max-width: 639px)");
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const data = window.matchMedia("(prefers-reduced-data: reduce)");
    const connection = (navigator as Navigator & { connection?: Connection }).connection;
    let disposed = false, failed = false, attached = false, inView = false;
    let imagePainted = false, pageLoaded = document.readyState === "complete";
    let imagePreparing = false;
    let playing = false, attempting = false, epoch = 0;
    let startup: ReturnType<typeof setTimeout> | undefined;
    let defer: ReturnType<typeof setTimeout> | undefined;
    let idle: number | undefined;
    let frame1: number | undefined, frame2: number | undefined;
    const eligible = () => mobile.matches && !motion.matches && !data.matches &&
      !connection?.saveData && (!connection?.effectiveType || connection.effectiveType === "4g") &&
      (connection?.downlink === undefined || connection.downlink >= 1.5);
    const active = () => eligible() && inView && document.visibilityState === "visible";
    const clearStartup = () => { clearTimeout(startup); startup = undefined; };
    const cancelDeferred = () => {
      clearTimeout(defer); defer = undefined;
      if (idle !== undefined) window.cancelIdleCallback?.(idle);
      idle = undefined;
    };
    const update = () => {
      if (!disposed) setVisible(playing);
    };
    const unload = () => {
      ++epoch;
      cancelDeferred(); clearStartup();
      playing = false; attempting = false;
      if (attached) {
        attached = false;
        video.pause();
        video.removeAttribute("src");
        video.load(); // Abort pending media transfers, including on desktop resize.
      }
      update();
    };
    const fail = () => { failed = true; unload(); };
    const armTimeout = () => {
      clearStartup();
      startup = setTimeout(fail, 6000);
    };
    const play = () => {
      if (!attached || !active() || failed || attempting || !video.paused) return;
      const current = epoch;
      attempting = true;
      armTimeout();
      try {
        const result = video.play();
        result?.catch(() => {
          // A visibility pause/unload may reject an old play promise. It must
          // not invalidate a subsequent visibility session.
          if (!disposed && current === epoch && active()) fail();
        }).finally(() => { if (current === epoch) attempting = false; });
      } catch { fail(); }
    };
    const attach = () => {
      if (disposed || failed || attached || !active() || !imagePainted || !pageLoaded) return;
      attached = true;
      video.muted = true;
      video.src = src;
      update();
      play();
    };
    const reconcile = () => {
      if (disposed || failed) return;
      if (!eligible()) { unload(); return; }
      if (!active()) {
        cancelDeferred(); clearStartup(); ++epoch; attempting = false;
        playing = false; video.pause(); update();
        return;
      }
      if (attached) { play(); return; }
      if (!imagePainted || !pageLoaded || defer !== undefined || idle !== undefined) return;
      // The image has painted and initial load is complete. Leave critical
      // work alone; never use a preload or a CSS-hidden download as the gate.
      defer = setTimeout(() => {
        defer = undefined;
        if (window.requestIdleCallback) {
          idle = window.requestIdleCallback(() => { idle = undefined; attach(); });
        } else attach();
      }, 1500);
    };
    const imageReady = () => {
      if (!image.complete || image.naturalWidth === 0 || imagePainted || imagePreparing) return;
      imagePreparing = true;
      // Two frames ensure the already decoded responsive image had a paint
      // opportunity. No second poster fetch is needed.
      const painted = () => {
        if (disposed) return;
        frame1 = requestAnimationFrame(() => {
          frame2 = requestAnimationFrame(() => { imagePainted = true; reconcile(); });
        });
      };
      if (typeof image.decode === "function") void image.decode().then(painted, () => {
        // A decode failure is not permission to compete with/retry the image.
        failed = true;
      });
      else painted();
    };
    const loaded = () => { pageLoaded = true; reconcile(); };
    const onPlaying = () => {
      if (!active() || failed || !attached) { video.pause(); return; }
      clearStartup(); attempting = false; playing = true; update();
    };
    const onWaiting = () => {
      // Keep the last decoded frame through brief loop/buffer boundaries;
      // showing the image immediately would flash a third scene each loop.
      // Initial startup remains image-only; a sustained stall still fails.
      if (attached && active()) armTimeout();
    };
    const onPause = () => { playing = false; update(); };
    const observer = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting; reconcile();
    });
    update();
    observer.observe(image);
    image.addEventListener("load", imageReady);
    window.addEventListener("load", loaded);
    document.addEventListener("visibilitychange", reconcile);
    for (const query of [mobile, motion, data]) query.addEventListener("change", reconcile);
    connection?.addEventListener("change", reconcile);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("pause", onPause);
    video.addEventListener("error", fail);
    imageReady();
    return () => {
      disposed = true;
      observer.disconnect();
      image.removeEventListener("load", imageReady);
      window.removeEventListener("load", loaded);
      document.removeEventListener("visibilitychange", reconcile);
      for (const query of [mobile, motion, data]) query.removeEventListener("change", reconcile);
      connection?.removeEventListener("change", reconcile);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("error", fail);
      if (frame1 !== undefined) cancelAnimationFrame(frame1);
      if (frame2 !== undefined) cancelAnimationFrame(frame2);
      unload();
    };
  }, [imageRef, src]);

  return <video ref={videoRef} aria-hidden="true" tabIndex={-1} muted playsInline loop
      preload="none" disablePictureInPicture disableRemotePlayback
      className="home-hero-video" data-playing={visible || undefined} />;
}
